import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaTrash, FaCheckCircle } from "react-icons/fa";

const AdminBrands = () => {
    const [loading, setLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const brandsEndRef = useRef(null);
    const gridRef = useRef(null);

    const defaultBrands = [
        "NIKE", "ADIDAS", "MLB", "CONVERSE", "BALENCIAGA", 
        "PUMA", "VANS", "NEW BALANCE", "FILA", "GUCCI", 
        "LV", "JORDAN BRAND", "ASICS", "RICK OWENS", "TIMBERLAND", "KHÁC"
    ];

    useEffect(() => {
        fetchBrands();
    }, []);

    // Tự động cuộn xuống khi thêm thương hiệu mới
    useEffect(() => {
        if (brands.length > 0 && brands[brands.length - 1] === "") {
            brandsEndRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            
            // Focus vào ô input cuối cùng
            setTimeout(() => {
                const inputs = gridRef.current?.querySelectorAll('input');
                if (inputs && inputs.length > 0) {
                    inputs[inputs.length - 1].focus();
                }
            }, 100);
        }
    }, [brands.length]);

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const response = await api.get("/settings/brands_config");
            console.log("DEBUG: AdminBrands response raw:", response.data);
            
            const extractBrands = (data) => {
                if (!data) return null;
                
                // Nếu là mảng
                if (Array.isArray(data)) {
                    // Truy tìm trong mảng xem có item nào có cấu trúc {Key: 'brands', Value: [...]} không
                    const brandsEntry = data.find(item => item && (item.Key === 'brands' || item.key === 'brands'));
                    if (brandsEntry && Array.isArray(brandsEntry.Value || brandsEntry.value)) {
                        return brandsEntry.Value || brandsEntry.value;
                    }

                    // Nếu là mảng chuỗi trực tiếp
                    if (data.length > 0 && typeof data[0] === 'string') {
                        return data;
                    }

                    // Thử đào sâu vào phần tử đầu tiên
                    if (data.length > 0) return extractBrands(data[0]);
                    return null;
                }

                // Nếu là object
                if (data.brands && Array.isArray(data.brands)) return data.brands;
                if (data.Brands && Array.isArray(data.Brands)) return data.Brands;
                if (data.value) return extractBrands(data.value);
                
                // Cấu trúc {Key: 'brands', Value: [...]} nhưng là object đơn lẻ
                if ((data.Key === 'brands' || data.key === 'brands') && Array.isArray(data.Value || data.value)) {
                    return data.Value || data.value;
                }

                return null;
            };

            const brandListArr = extractBrands(response.data);

            if (brandListArr && brandListArr.length > 0) {
                console.log("DEBUG: AdminBrands extracted brands:", brandListArr);
                setBrands(brandListArr);
            } else {
                console.log("DEBUG: Không tìm thấy mảng brands phù hợp, dùng mặc định");
                setBrands(defaultBrands);
            }
        } catch (error) {
            console.error("Lỗi khi tải thương hiệu:", error);
            setBrands(defaultBrands);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            // Làm sạch dữ liệu trước khi lưu
            const cleanBrands = brands
                .map(b => b.trim().toUpperCase())
                .filter(b => b !== "");
            
            await api.put("/admin/settings/brands_config", { brands: cleanBrands });
            setBrands(cleanBrands);
            toast.success("Cập nhật danh sách thương hiệu thành công!");
            // Thông báo cho các component khác
            window.dispatchEvent(new Event('brands_updated'));
        } catch (error) {
            toast.error("Không thể lưu cấu hình");
        } finally {
            setIsSaving(false);
        }
    };

    const addBrand = () => {
        setBrands([...brands, ""]);
    };

    const removeBrand = (index) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa thương hiệu này?")) {
            const newBrands = brands.filter((_, i) => i !== index);
            setBrands(newBrands);
        }
    };

    const updateBrand = (index, value) => {
        const newBrands = [...brands];
        newBrands[index] = value;
        setBrands(newBrands);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Quản lý Thương hiệu</h1>
                    <p className="text-gray-500 font-medium">Danh mục này sẽ hiển thị trên Navbar và trang thêm sản phẩm</p>
                </div>
                <button
                    onClick={addBrand}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                >
                    <FaPlus size={14} />
                    <span>Thêm thương hiệu</span>
                </button>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-6 sm:p-8">
                <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {brands.map((brand, idx) => (
                        <div key={idx} className="relative group animate-fadeIn">
                            <input
                                type="text"
                                value={brand}
                                onChange={(e) => updateBrand(idx, e.target.value)}
                                placeholder="Nhập tên thương hiệu..."
                                className="w-full bg-gray-50 border border-transparent focus:bg-white focus:border-red-500 rounded-xl px-4 py-3 outline-none transition-all font-bold uppercase tracking-wide text-sm pr-10"
                            />
                            <button
                                onClick={() => removeBrand(idx)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                            >
                                <FaTrash size={14} />
                            </button>
                        </div>
                    ))}
                    <div ref={brandsEndRef} />
                    {brands.length === 0 && (
                        <div className="col-span-full py-12 text-center text-gray-400 italic">
                            Chưa có thương hiệu nào. Nhấn "Thêm" để bắt đầu.
                        </div>
                    )}
                </div>

                <div className="pt-8 border-t border-gray-100">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="w-full bg-red-600 text-white rounded-2xl py-4 font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-500/20 flex items-center justify-center gap-3 disabled:bg-gray-400 disabled:shadow-none hover:scale-[1.01] active:scale-[0.99]"
                    >
                        {isSaving ? (
                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <FaSave size={20} />
                        )}
                        <span>{isSaving ? "Đang lưu..." : "Lưu cấu hình thương hiệu"}</span>
                    </button>
                    
                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-green-600 font-bold bg-green-50 py-2 rounded-lg">
                        <FaCheckCircle />
                        <span>Các thay đổi sẽ hiệu lực ngay lập tức trên trang chủ</span>
                    </div>
                </div>
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-100 rounded-2xl p-6">
                <h4 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm">info</span>
                    Lưu ý cho Admin:
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc ml-5">
                    <li>Nên viết hoa tên thương hiệu để hiển thị đẹp nhất.</li>
                    <li>Sắp xếp các thương hiệu phổ biến lên hàng đầu.</li>
                    <li>Sau khi xóa một thương hiệu, các sản phẩm đang gắn thương hiệu đó vẫn giữ nguyên dữ liệu nhưng bạn nên cập nhật lại danh mục cho chúng.</li>
                </ul>
            </div>
        </div>
    );
};

export default AdminBrands;
